import React, { useCallback, useContext } from 'react';
import tw from 'tailwind-styled-components';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button } from '@boozallen-uip/uswds-react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faXmark, faUserClock, faUserCheck, faBell, faCircleExclamation, faUserXmark } from '@fortawesome/free-solid-svg-icons';
import {
  ENTITY_APPROVAL_URL,
  WELCOME_URL,
  NOTIFICATION_TYPE_PENDING_ACCOUNT_REQUEST,
  NOTIFICATION_TYPE_PENDING_ACCOUNT_REQUEST_REMINDER,
  NOTIFICATION_TYPE_ACCOUNT_ROLE_ASSIGNMENT,
  NOTIFICATION_TYPE_ACCOUNT_REQUEST_DENIED,
  NOTIFICATION_TYPE_PENDING_ACCOUNT_DEACTIVATION,
  NOTIFICATION_TYPE_ACCOUNT_DEACTIVATED
} from '../../utlities/core.constants';
import useNotification from '../../hooks/useNotification.hook';
import { NotificationContext } from './NotificationContext';

const NotificationContainer = tw.div`
  grid grid-cols-12 border-2 border-gray-lighter mb-4 rounded-md p-4 gap-x-3
  ${props => props.$isRead ? 'bg-white' : 'bg-blue-50'}
`;

const getNotificationLink = (notification) => {
  switch (notification.eventType) {
    case NOTIFICATION_TYPE_PENDING_ACCOUNT_REQUEST:
    case NOTIFICATION_TYPE_PENDING_ACCOUNT_REQUEST_REMINDER:
      return ENTITY_APPROVAL_URL;
    case NOTIFICATION_TYPE_ACCOUNT_REQUEST_DENIED:
      return ENTITY_APPROVAL_URL;
    case NOTIFICATION_TYPE_ACCOUNT_ROLE_ASSIGNMENT:
      return WELCOME_URL;
    case NOTIFICATION_TYPE_ACCOUNT_DEACTIVATED:
      return ENTITY_APPROVAL_URL;
    default:
      return null;
  }
};

const getNotificationIcon = (eventType) => {
  switch (eventType) {
    case NOTIFICATION_TYPE_PENDING_ACCOUNT_REQUEST:
    case NOTIFICATION_TYPE_PENDING_ACCOUNT_REQUEST_REMINDER:
      return faUserClock;
    case NOTIFICATION_TYPE_ACCOUNT_ROLE_ASSIGNMENT:
      return faUserCheck;
    case NOTIFICATION_TYPE_PENDING_ACCOUNT_DEACTIVATION:
      return faCircleExclamation;
    case NOTIFICATION_TYPE_ACCOUNT_REQUEST_DENIED:
      return faUserXmark;
    case NOTIFICATION_TYPE_ACCOUNT_DEACTIVATED:
      return faUserXmark;
    default:
      return faBell;
  }
};

const getNotificationLinkText = (eventType) => {
  switch (eventType) {
    case NOTIFICATION_TYPE_ACCOUNT_REQUEST_DENIED:
      return 'Go to Manage Users page for more information';
    default:
      return 'View details \u2192';
  }
};

const ACCOUNT_REQUEST_DENIED_DETAILS_SUFFIXES = [
  '. Go to the Manage Users page for more information.',
  '. Go to the Manage Users page.'
];

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const renderNotificationMessage = (notification) => {
  if (notification.eventType !== NOTIFICATION_TYPE_ACCOUNT_REQUEST_DENIED) {
    return notification.message;
  }

  const denialPrefix = ' has been denied due to ';
  const prefixIndex = notification.message.indexOf(denialPrefix);
  const detailsPrefix = ACCOUNT_REQUEST_DENIED_DETAILS_SUFFIXES.find((suffix) => notification.message.includes(suffix));
  const detailsIndex = detailsPrefix ? notification.message.lastIndexOf(detailsPrefix) : -1;

  if (prefixIndex === -1 || !detailsPrefix || detailsIndex === -1 || detailsIndex <= prefixIndex) {
    return notification.message;
  }

  const beforeReason = notification.message.slice(0, prefixIndex + denialPrefix.length);
  const reason = notification.message.slice(prefixIndex + denialPrefix.length, detailsIndex);
  const afterReason = notification.message.slice(detailsIndex);

  return (
    <>
      {beforeReason}
      <em>{reason}</em>
      {afterReason}
    </>
  );
};

export default function NotificationItem({ notification, onClear, onRead }) {
  const navigate = useNavigate();
  const { markAsRead, clearNotification } = useNotification();
  const { decrementActiveCount } = useContext(NotificationContext);

  const handleClick = useCallback(async () => {
    // Mark as read if not already
    if (!notification.isRead) {
      const success = await markAsRead(notification.id);
      if (success && onRead) onRead(notification.id);
    }

    // Navigate to related page
    const link = getNotificationLink(notification);
    if (link) {
      navigate(link);
    }
  }, [notification, markAsRead, navigate, onRead]);

  const handleClear = useCallback(async (e) => {
    e.stopPropagation();
    const success = await clearNotification(notification.id);
    if (success) {
      decrementActiveCount();
      if (onClear) onClear(notification.id);
    }
  }, [notification, clearNotification, decrementActiveCount, onClear]);

  const link = getNotificationLink(notification);

  return (
    <NotificationContainer $isRead={notification.isRead}>
      <div className='col-start-1 col-span-1 flex items-center justify-center' aria-hidden="true">
        <FontAwesomeIcon icon={getNotificationIcon(notification.eventType)} className="text-2xl text-gray-400" />
      </div>
      <div
        className='col-span-9 cursor-pointer focus:outline-2 focus:outline-primary focus:outline-offset-2 rounded'
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`${notification.isRead ? '' : 'Unread: '}${notification.title}. ${notification.message}. ${formatDate(notification.createdDate)}${link ? '. Click to view details' : ''}`}
      >
        <div className='flex items-center gap-2'>
          {!notification.isRead && (
            <>
              <FontAwesomeIcon icon={faCircle} className="text-primary text-xs" aria-hidden="true" />
              <span className="sr-only">Unread</span>
            </>
          )}
          <span className={`font-bold ${!notification.isRead ? 'text-primary-darker' : 'text-gray-700'}`}>
            {notification.title}
          </span>
        </div>
        <p className='text-gray-600 mt-1 mb-1'>{renderNotificationMessage(notification)}</p>
        <p className='text-gray-400 text-sm'>
          {formatDate(notification.createdDate)}
        </p>
        {link && (
          <span className='text-primary text-sm mt-2 inline-block'>
            {getNotificationLinkText(notification.eventType)}
          </span>
        )}
      </div>
      <div className='col-span-2 flex items-center justify-end'>
        <Button
          unstyled
          onClick={handleClear}
          aria-label="Clear notification"
          className="text-gray-400 hover:text-gray-600 p-2"
        >
          <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
        </Button>
      </div>
    </NotificationContainer>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    eventType: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    isRead: PropTypes.bool.isRequired,
    createdDate: PropTypes.string.isRequired,
    createdBy: PropTypes.string
  }).isRequired,
  onClear: PropTypes.func,
  onRead: PropTypes.func
};
